import { useState } from "react";
import { useNavigate } from "react-router";
import { errorText, post, refresh, useApi } from "../lib/api.ts";
import { PERMISSION_MODES, PERMISSION_MODE_HELP } from "../lib/format.ts";
import type { EnvFile, FolderListing, Project, Session, Settings, SystemStatus } from "../lib/types.ts";
import { EffortSelect, EnvFilesEditor, envFilesError, FolderPicker, ModelPicker } from "./Pickers.tsx";
import { Field, Modal, Toggle, useToast } from "./ui.tsx";

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

export function ModeSelect({ value, onChange, allowInherit }: { value: string; onChange: (v: string) => void; allowInherit?: string }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {allowInherit !== undefined && <option value="">Project default ({allowInherit})</option>}
      {PERMISSION_MODES.map((m) => (
        <option key={m} value={m}>
          {m} · {PERMISSION_MODE_HELP[m]}
        </option>
      ))}
    </select>
  );
}

const DEFAULT_TEMPLATE = `# Working agreement

- Work only inside this worktree.
- Run the tests before you say you are done.
- Keep commits small and explain why in the message.
`;

/** The CLAUDE.md a workspace starts with: its repositories and how to commit across them. */
export function workspaceTemplate(name: string, repos: string[]): string {
  const list = repos.length ? repos.map((r) => `- \`${r}/\``).join("\n") : "- (add the repositories here)";
  return `# ${name || "Workspace"}

This folder is a workspace holding separate git repositories:

${list}

## Rules

- Each folder above is its own git repository. Run git commands from inside that folder, and commit each repository's changes separately with its own message.
- The workspace root is a small repository of its own that tracks only workspace files such as this CLAUDE.md. Never \`git add\` the repository folders from here.
- When a change spans repositories (an API change and its client, say), make it in each one and mention the other in the commit message.
- Run each repository's own tests before you say you are done.
`;
}

interface CloneRow {
  path: string;
  url: string;
}

export function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const settings = useApi<Settings>("/api/settings");
  const navigate = useNavigate();
  const toast = useToast();
  const root = (settings.data?.["server.projects_root"] as string | null) ?? "";
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [kind, setKind] = useState<"repo" | "workspace">("repo");
  const [source, setSource] = useState<"existing" | "clone">("existing");
  const [repoUrl, setRepoUrl] = useState("");
  const [path, setPath] = useState("");
  // Workspace: which sub-folders are repositories (undefined = default: the ones that are already git repos), plus clones.
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [clones, setClones] = useState<CloneRow[]>([]);
  const [claudeMd, setClaudeMd] = useState<string | null>(null);
  const [envFiles, setEnvFiles] = useState<EnvFile[]>([]);
  // null = not touched: show (and save) what Settings → Claude says.
  const [model, setModel] = useState<string | null>(null);
  const [effort, setEffort] = useState<string | null>(null);
  const [mode, setMode] = useState<string | null>(null);
  const [maxSessions, setMaxSessions] = useState<number | null>(null);
  const [useTemplate, setUseTemplate] = useState(false);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const folder = path.trim() || effectiveSlug;
  const s = (key: string) => settings.data?.[key];
  const effectiveModel = model ?? ((s("claude.default_model") as string | null) || "");
  const effectiveEffort = effort ?? ((s("claude.default_effort") as string | null) || "");
  const effectiveMode = mode ?? ((s("claude.default_permission_mode") as string) || "acceptEdits");
  const effectiveMax = maxSessions ?? Math.min(Number(s("claude.max_concurrent") ?? 3) || 3, 32);
  const slugOk = SLUG_RE.test(effectiveSlug);

  const isWorkspace = kind === "workspace";
  const listing = useApi<FolderListing>(isWorkspace && root && folder ? `/api/folders?path=${encodeURIComponent(folder)}` : null, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });
  const existing = listing.data?.folders ?? [];
  const chosen = existing.filter((f) => picked[f.name] ?? f.isGit).map((f) => f.name);
  const cloneRows = clones.filter((c) => c.path.trim() || c.url.trim());
  const repoPaths = [...chosen, ...cloneRows.map((c) => c.path.trim())].filter(Boolean);
  const effectiveClaudeMd = claudeMd ?? listing.data?.claudeMd ?? workspaceTemplate(name.trim(), repoPaths);
  const envError = envFilesError(envFiles);
  const workspaceError = !isWorkspace
    ? undefined
    : !repoPaths.length
      ? "Pick at least one repository folder, or clone one into the workspace."
      : cloneRows.some((c) => !c.path.trim() || !c.url.trim())
        ? "Each clone needs a folder name and a URL."
        : new Set(repoPaths).size !== repoPaths.length
          ? "Two repositories use the same folder."
          : !effectiveClaudeMd.trim()
            ? "A workspace needs a CLAUDE.md."
            : undefined;

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const where = isWorkspace
        ? {
            existingPath: folder,
            repos: [...chosen.map((p) => ({ path: p })), ...cloneRows.map((c) => ({ path: c.path.trim(), repoUrl: c.url.trim() }))],
            claudeMd: effectiveClaudeMd,
          }
        : source === "clone"
          ? { repoUrl: repoUrl.trim() }
          : { existingPath: folder };
      const project = await post<Project & { channelError?: string }>("/api/projects", {
        name: name.trim(),
        slug: effectiveSlug,
        kind,
        ...where,
        defaultModel: effectiveModel.trim() || undefined,
        defaultEffort: effectiveEffort || undefined,
        permissionMode: effectiveMode,
        maxSessions: effectiveMax,
        envFiles: envFiles
          .filter((f) => !isWorkspace || !f.repo || repoPaths.includes(f.repo))
          .map((f) => ({ repo: isWorkspace ? f.repo : "", path: f.path.trim(), content: f.content })),
        claudeMdTemplate: !isWorkspace && useTemplate ? template : undefined,
      });
      refresh("/api/projects", "/api/overview");
      if (project.channelError) toast(`Project created, but the Discord channel failed: ${project.channelError}`, "bad");
      else toast(project.channelId ? `Created ${project.name} and #${project.slug}` : `Created ${project.name}`);
      onClose();
      navigate(`/projects/${project.slug}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const disabled =
    busy || !name.trim() || !slugOk || (!isWorkspace && source === "clone" && !repoUrl.trim()) || Boolean(workspaceError) || Boolean(envError);
  const rootJoin = (p: string) => `${root}${root.endsWith("/") || root.endsWith("\\") ? "" : "/"}${p}`;

  return (
    <Modal
      title="New project"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={disabled} title={workspaceError ?? envError ?? ""} onClick={create}>
            {busy ? (isWorkspace ? "Setting up workspace…" : source === "clone" ? "Cloning…" : "Creating…") : "Create project"}
          </button>
        </>
      }
    >
      {!root && <div className="callout warn">Set the projects root in Settings → Server first. Projects must live under it.</div>}
      <div className="grid2">
        <Field label="Name">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Agency OS" />
        </Field>
        <Field label="Slug · channel name" error={effectiveSlug && !slugOk ? "lowercase letters, digits and dashes" : undefined}>
          <input
            className="mono"
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
            placeholder="agency-os"
          />
        </Field>
      </div>
      <div className="field">
        <span>Layout</span>
        <div className="seg" role="radiogroup">
          <button type="button" className={!isWorkspace ? "on" : ""} onClick={() => setKind("repo")}>
            One repository
          </button>
          <button type="button" className={isWorkspace ? "on" : ""} onClick={() => setKind("workspace")}>
            Workspace: several repositories in one folder
          </button>
        </div>
        <span className="hint">
          {isWorkspace
            ? "e.g. agency-os/frontend and agency-os/backend. Each session gets every repository on its own branch; each repository is committed separately."
            : "A single git checkout."}
        </span>
      </div>

      {!isWorkspace ? (
        <>
          <div className="field">
            <span>Source</span>
            <div className="seg" role="radiogroup">
              <button type="button" className={source === "existing" ? "on" : ""} onClick={() => setSource("existing")}>
                Existing folder on the server
              </button>
              <button type="button" className={source === "clone" ? "on" : ""} onClick={() => setSource("clone")}>
                Clone a repository
              </button>
            </div>
          </div>
          {source === "existing" ? (
            <FolderPicker
              label="Folder (relative to the projects root)"
              value={path}
              onChange={setPath}
              placeholder={effectiveSlug || "agency-os"}
              hint={root ? `Resolves to ${rootJoin(folder || "<slug>")}` : undefined}
            />
          ) : (
            <Field label="Repository URL" hint={`Cloned into the projects root as ${effectiveSlug || "<slug>"}`}>
              <input className="mono" value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)} placeholder="https://github.com/you/repo.git" />
            </Field>
          )}
        </>
      ) : (
        <>
          <FolderPicker
            label="Workspace folder (relative to the projects root)"
            value={path}
            onChange={(v) => {
              setPath(v);
              setPicked({});
              setClaudeMd(null);
            }}
            placeholder={effectiveSlug || "agency-os"}
            hint={root ? `${rootJoin(folder || "<slug>")}. Browse to create a new folder, or pick one that already holds your repositories.` : undefined}
          />
          <WorkspaceRepos
            listing={listing.data}
            missing={Boolean(listing.error)}
            picked={picked}
            onPick={(n, v) => setPicked((p) => ({ ...p, [n]: v }))}
            clones={clones}
            onClones={setClones}
          />
          <Field
            label="CLAUDE.md for the workspace (required)"
            hint={
              listing.data?.claudeMd != null
                ? "The folder already has a CLAUDE.md; edits here are committed to the workspace repository."
                : "The rules every session follows across the repositories. Committed to the workspace repository."
            }
            error={!effectiveClaudeMd.trim() ? "required for a workspace" : undefined}
          >
            <textarea className="mono" rows={10} value={effectiveClaudeMd} onChange={(e) => setClaudeMd(e.target.value)} />
          </Field>
        </>
      )}

      <EnvFilesEditor files={envFiles} onChange={setEnvFiles} repos={isWorkspace ? repoPaths : []} />

      <div className="grid2">
        <Field label="Default model" hint="Filled in from Settings → Claude.">
          <ModelPicker value={effectiveModel} onChange={setModel} />
        </Field>
        <Field label="Effort">
          <EffortSelect value={effectiveEffort} onChange={setEffort} />
        </Field>
        <Field label="Permission mode">
          <ModeSelect value={effectiveMode} onChange={setMode} />
        </Field>
        <Field label="Max sessions">
          <input type="number" min={1} max={32} value={effectiveMax} onChange={(e) => setMaxSessions(Number(e.target.value) || 1)} />
        </Field>
      </div>
      {!isWorkspace && (
        <>
          <label className="check">
            <input type="checkbox" checked={useTemplate} onChange={(e) => setUseTemplate(e.target.checked)} />
            <span>Add a CLAUDE.md template to each new worktree (when the repo has none)</span>
          </label>
          {useTemplate && <textarea className="mono" rows={6} value={template} onChange={(e) => setTemplate(e.target.value)} />}
        </>
      )}
      <div className="muted small">The bot creates #{effectiveSlug || "slug"} under the Discord category when it is online.</div>
      {(error || envError) && <div className="callout bad">{error ?? envError}</div>}
    </Modal>
  );
}

function WorkspaceRepos({
  listing,
  missing,
  picked,
  onPick,
  clones,
  onClones,
}: {
  listing?: FolderListing;
  missing: boolean;
  picked: Record<string, boolean>;
  onPick: (name: string, on: boolean) => void;
  clones: CloneRow[];
  onClones: (rows: CloneRow[]) => void;
}) {
  const setClone = (i: number, patch: Partial<CloneRow>) => onClones(clones.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  return (
    <div className="field">
      <span>Repositories</span>
      {missing ? (
        <span className="hint">The folder does not exist yet; it will be created. Clone repositories into it below.</span>
      ) : listing ? (
        <span className="hint">
          {listing.isGit
            ? "The workspace folder is a git repository; only its CLAUDE.md gets committed there."
            : "The workspace folder is not a git repository yet: Switchboard runs git init in it, ignoring the repository folders."}
        </span>
      ) : null}
      {listing && listing.folders.length > 0 && (
        <div className="card" style={{ padding: 10, gap: 6 }}>
          {listing.folders.map((f) => (
            <label key={f.name} className="check">
              <input type="checkbox" checked={picked[f.name] ?? f.isGit} onChange={(e) => onPick(f.name, e.target.checked)} />
              <span className="row nowrap">
                <span className="mono">{f.name}/</span>
                {f.isGit ? <span className="pill ok">git</span> : <span className="pill warn">no git yet: will be initialised and committed</span>}
              </span>
            </label>
          ))}
        </div>
      )}
      {clones.map((c, i) => (
        <div key={i} className="row nowrap">
          <input className="mono" style={{ maxWidth: 180 }} value={c.path} placeholder="backend" onChange={(e) => setClone(i, { path: e.target.value })} />
          <input
            className="mono"
            value={c.url}
            placeholder="https://github.com/you/backend.git"
            onChange={(e) => {
              // Keep the folder name in step with the URL until someone types their own.
              const url = e.target.value;
              setClone(i, { url, ...(c.path && c.path !== guessFrom(c.url) ? {} : { path: guessFrom(url) }) });
            }}
          />
          <button type="button" className="btn ghost sm" onClick={() => onClones(clones.filter((_, j) => j !== i))}>
            Remove
          </button>
        </div>
      ))}
      <div>
        <button type="button" className="btn sm" onClick={() => onClones([...clones, { path: "", url: "" }])}>
          + Clone a repository into the workspace
        </button>
      </div>
    </div>
  );
}

const guessFrom = (url: string) => url.split(/[/:]/).filter(Boolean).at(-1)?.replace(/\.git$/, "") ?? "";

export function NewSessionDialog({ project, onClose }: { project?: Project; onClose: () => void }) {
  const projects = useApi<Project[]>(project ? null : "/api/projects");
  const navigate = useNavigate();
  const [projectSlug, setProjectSlug] = useState(project?.slug ?? "");
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("");
  const [effort, setEffort] = useState("");
  const [base, setBase] = useState("");
  const [mode, setMode] = useState("");
  const [remote, setRemote] = useState(true);
  const [newBranch, setNewBranch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const system = useApi<SystemStatus>("/api/system", { revalidateOnFocus: false });
  const canRemote = system.data ? system.data.launcher === "tmux" : true;

  const list = project ? [project] : (projects.data ?? []).filter((p) => p.status === "active");
  const target = list.find((p) => p.slug === projectSlug) ?? (list.length === 1 ? list[0] : undefined);
  const fromPrompt = slugify(prompt.split(/\s+/).slice(0, 5).join(" ")).slice(0, 40);
  const effectiveName = name || fromPrompt;
  const nameOk = SLUG_RE.test(effectiveName);

  const create = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const s = await post<Session>(`/api/projects/${target.slug}/sessions`, {
        name: effectiveName,
        prompt: prompt.trim() || undefined,
        model: model.trim() || undefined,
        effort: effort || undefined,
        baseBranch: (newBranch && base.trim()) || undefined,
        permissionMode: mode || undefined,
        remote: remote && canRemote,
        newBranch,
      });
      refresh("/api/sessions", "/api/overview", "/api/projects/");
      onClose();
      navigate(`/sessions/${s.id}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New session"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || !target || !nameOk} onClick={create}>
            {busy ? (newBranch ? "Creating worktree…" : "Starting…") : "Start session"}
          </button>
        </>
      }
    >
      {!project && (
        <Field label="Project">
          <select value={target?.slug ?? ""} onChange={(e) => setProjectSlug(e.target.value)}>
            <option value="" disabled>
              Pick a project
            </option>
            {list.map((p) => (
              <option key={p.id} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Toggle
        checked={remote && canRemote}
        disabled={!canRemote}
        onChange={setRemote}
        label="Remote Control"
        hint={
          canRemote
            ? "Runs Claude Code in its terminal with Remote Control, so you can also open the session in the Claude app. Questions and permission prompts are answered there or in the terminal, not here."
            : "Needs tmux on the server."
        }
      />
      <Toggle
        checked={newBranch}
        onChange={setNewBranch}
        label="New branch"
        hint={
          newBranch
            ? `Creates branch session/${effectiveName || "<name>"} in its own worktree, so it doesn't touch other sessions' files.`
            : "Off: works in the project checkout on whatever branch it has now, sharing its files with other sessions there."
        }
      />
      <Field label="First prompt" hint="Optional. Sent as soon as the runner is up. Ctrl+Enter to start.">
        <textarea
          autoFocus
          rows={5}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.ctrlKey || e.metaKey) && target && nameOk && create()}
          placeholder="The login page redirects to /dashboard even when the user has no workspace…"
        />
      </Field>
      <div className="grid2">
        <Field label={newBranch ? "Name (branch session/<name>)" : "Name"} error={effectiveName && !nameOk ? "lowercase letters, digits and dashes" : undefined}>
          <input className="mono" value={effectiveName} onChange={(e) => setName(e.target.value)} placeholder="fix-login-redirect" />
        </Field>
        {newBranch && (
          <Field
            label="Base branch"
            hint={target?.kind === "workspace" ? "Default: each repository's current branch. Repositories without this branch use theirs." : "Default: the project checkout's current branch"}
          >
            <input className="mono" value={base} onChange={(e) => setBase(e.target.value)} placeholder="main" />
          </Field>
        )}
        <Field label="Model">
          <ModelPicker value={model} onChange={setModel} inheritLabel={`Project default (${target?.defaultModel || "Claude Code's default"})`} />
        </Field>
        <Field label="Effort">
          <EffortSelect value={effort} onChange={setEffort} inheritLabel={`Project default (${target?.defaultEffort || "model default"})`} />
        </Field>
        <Field label="Permission mode">
          <ModeSelect value={mode} onChange={setMode} allowInherit={target?.permissionMode ?? "acceptEdits"} />
        </Field>
      </div>
      {error && <div className="callout bad">{error}</div>}
    </Modal>
  );
}
